// services/attendanceService.js

import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    query,
    where,
    Timestamp,
    orderBy,
    limit
} from "firebase/firestore";
import { db } from "../network/firebaseConfig";

const ATTENDANCE_COLLECTION = "attendance";

// Get today's date string
export const getTodayString = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
};

// Generate session ID
const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
};

// Get or create today's attendance record
export const getOrCreateTodayAttendance = async (user) => {
    const today = getTodayString();
    const docId = `${user.uid}_${today}`;
    const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

    const snapshot = await getDoc(docRef);

    if (snapshot.exists()) {
        return { id: snapshot.id, ...snapshot.data() };
    }

    const newRecord = {
        userId: user.uid,
        userName: user.name || "Unknown",
        email: user.email,
        date: today,
        sessions: [],
        totalWorkDuration: 0,
        totalBreakTime: 0,
        overtime: {
            isOvertime: false,
            minutes: 0,
            approved: false,
            approvedBy: null,
            approvedAt: null
        },
        status: "not-checked-in",
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
    };

    await setDoc(docRef, newRecord);
    return { id: docId, ...newRecord };
};

// Get active session
const getActiveSession = (sessions) => {
    return sessions?.find(s => !s.checkOut);
};

// Check if user has any active session
export const hasActiveSession = (attendance) => {
    if (!attendance?.sessions) return false;
    return attendance.sessions.some(s => !s.checkOut);
};

// Check-in (creates a new session)
export const checkIn = async (userId, location, deviceInfo) => {
    const today = getTodayString();
    const docId = `${userId}_${today}`;
    const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

    const snapshot = await getDoc(docRef);
    const existing = snapshot.exists() ? snapshot.data() : null;

    // Check if there's already an active session
    if (existing && hasActiveSession(existing)) {
        throw new Error("You already have an active session. Please check out first.");
    }

    // Check if max sessions per day reached (optional: limit to 3)
    const sessionCount = existing?.sessions?.length || 0;
    if (sessionCount >= 3) {
        throw new Error("Maximum 3 sessions per day allowed");
    }

    const checkInData = {
        time: Timestamp.now(),
        location: location || null,
        deviceInfo: deviceInfo || null
    };

    const newSession = {
        id: generateSessionId(),
        checkIn: checkInData,
        checkOut: null,
        breaks: [],
        duration: 0,
        isOvertime: false
    };

    if (existing) {
        const sessions = [...(existing.sessions || []), newSession];
        await updateDoc(docRef, {
            sessions: sessions,
            status: "checked-in",
            updatedAt: Timestamp.now()
        });
        return { id: docId, ...existing, sessions, status: "checked-in" };
    } else {
        const newRecord = {
            userId: userId,
            date: today,
            sessions: [newSession],
            totalWorkDuration: 0,
            totalBreakTime: 0,
            overtime: {
                isOvertime: false,
                minutes: 0,
                approved: false,
                approvedBy: null,
                approvedAt: null
            },
            status: "checked-in",
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        };
        await setDoc(docRef, newRecord);
        return { id: docId, ...newRecord };
    }
};

// Start break for current session
export const startBreak = async (userId) => {
    const today = getTodayString();
    const docId = `${userId}_${today}`;
    const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) {
        throw new Error("No attendance record found. Please check-in first.");
    }

    const data = snapshot.data();
    if (data.status !== "checked-in") {
        throw new Error("You must be checked in to take a break");
    }

    const sessions = data.sessions || [];
    const activeSession = getActiveSession(sessions);
    if (!activeSession) {
        throw new Error("No active session found");
    }

    // Check if there's an ongoing break in this session
    const activeBreak = activeSession.breaks?.find(b => !b.end);
    if (activeBreak) {
        throw new Error("You're already on a break");
    }

    const newBreak = {
        start: Timestamp.now(),
        end: null,
        duration: 0
    };

    // Update the active session's breaks
    const sessionIndex = sessions.indexOf(activeSession);
    sessions[sessionIndex] = {
        ...activeSession,
        breaks: [...(activeSession.breaks || []), newBreak]
    };

    await updateDoc(docRef, {
        sessions: sessions,
        status: "on-break",
        updatedAt: Timestamp.now()
    });

    return { ...data, sessions, status: "on-break" };
};

// End break for current session
export const endBreak = async (userId) => {
    const today = getTodayString();
    const docId = `${userId}_${today}`;
    const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) {
        throw new Error("No attendance record found");
    }

    const data = snapshot.data();
    if (data.status !== "on-break") {
        throw new Error("You're not on a break");
    }

    const sessions = data.sessions || [];
    const activeSession = getActiveSession(sessions);
    if (!activeSession) {
        throw new Error("No active session found");
    }

    const breaks = activeSession.breaks || [];
    const lastBreakIndex = breaks.length - 1;
    const lastBreak = breaks[lastBreakIndex];

    if (!lastBreak || lastBreak.end) {
        throw new Error("No active break found");
    }

    const endTime = Timestamp.now();
    const startTime = lastBreak.start;
    const durationMinutes = (endTime.seconds - startTime.seconds) / 60;

    breaks[lastBreakIndex] = {
        ...lastBreak,
        end: endTime,
        duration: Math.round(durationMinutes)
    };

    // Update the active session
    const sessionIndex = sessions.indexOf(activeSession);
    sessions[sessionIndex] = {
        ...activeSession,
        breaks: breaks
    };

    // Calculate total break time across all sessions
    const totalBreakMinutes = sessions.reduce((sum, s) =>
        sum + (s.breaks || []).reduce((bSum, b) => bSum + (b.duration || 0), 0), 0
    );

    await updateDoc(docRef, {
        sessions: sessions,
        totalBreakTime: totalBreakMinutes,
        status: "checked-in",
        updatedAt: Timestamp.now()
    });

    return { ...data, sessions, totalBreakTime: totalBreakMinutes, status: "checked-in" };
};

// Check-out (Leave) - ends the current session
export const checkOut = async (userId, location, isAuto = false) => {
    const today = getTodayString();
    const docId = `${userId}_${today}`;
    const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) {
        throw new Error("No attendance record found");
    }

    const data = snapshot.data();
    const sessions = data.sessions || [];
    const activeSession = getActiveSession(sessions);

    if (!activeSession) {
        throw new Error("No active session to check out");
    }

    // If on break, end break first
    if (data.status === "on-break") {
        const breaks = activeSession.breaks || [];
        const lastBreakIndex = breaks.length - 1;
        const lastBreak = breaks[lastBreakIndex];
        if (lastBreak && !lastBreak.end) {
            const endTime = Timestamp.now();
            const durationMinutes = (endTime.seconds - lastBreak.start.seconds) / 60;
            breaks[lastBreakIndex] = {
                ...lastBreak,
                end: endTime,
                duration: Math.round(durationMinutes)
            };
            activeSession.breaks = breaks;
        }
    }

    const checkOutData = {
        time: Timestamp.now(),
        location: location || null,
        isAuto: isAuto
    };

    // Calculate session duration (excluding breaks)
    const checkInTime = activeSession.checkIn.time;
    const checkOutTime = checkOutData.time;
    const totalMinutes = (checkOutTime.seconds - checkInTime.seconds) / 60;
    const sessionBreakMinutes = (activeSession.breaks || []).reduce((sum, b) => sum + (b.duration || 0), 0);
    const sessionWorkMinutes = Math.round(totalMinutes - sessionBreakMinutes);

    // Check if this session is overtime (after 10 PM)
    const checkOutHour = new Date(checkOutTime.seconds * 1000).getHours();
    const isOvertime = checkOutHour >= 22;

    // Update the active session
    const sessionIndex = sessions.indexOf(activeSession);
    sessions[sessionIndex] = {
        ...activeSession,
        checkOut: checkOutData,
        duration: sessionWorkMinutes,
        isOvertime: isOvertime
    };

    // Calculate total work duration across all sessions
    const totalWorkDuration = sessions.reduce((sum, s) => sum + (s.duration || 0), 0);

    // Calculate total break time across all sessions
    const totalBreakMinutes = sessions.reduce((sum, s) =>
        sum + (s.breaks || []).reduce((bSum, b) => bSum + (b.duration || 0), 0), 0
    );

    // Calculate total overtime (only from sessions that ended after 10 PM)
    const totalOvertimeMinutes = sessions
        .filter(s => s.isOvertime && s.checkOut)
        .reduce((sum, s) => {
            const checkOutTime = s.checkOut.time;
            const checkInTime = s.checkIn.time;
            const totalMinutes = (checkOutTime.seconds - checkInTime.seconds) / 60;
            const breakMinutes = (s.breaks || []).reduce((bSum, b) => bSum + (b.duration || 0), 0);
            const workMinutes = totalMinutes - breakMinutes;

            // Calculate overtime (time after 10 PM)
            const checkOutDate = new Date(checkOutTime.seconds * 1000);
            const tenPM = new Date(checkOutDate);
            tenPM.setHours(22, 0, 0, 0);
            if (checkOutDate > tenPM) {
                const overtimeMs = checkOutDate - tenPM;
                return sum + Math.round(overtimeMs / (1000 * 60));
            }
            return sum;
        }, 0);

    const updateData = {
        sessions: sessions,
        totalWorkDuration: totalWorkDuration,
        totalBreakTime: totalBreakMinutes,
        status: "checked-out",
        updatedAt: Timestamp.now()
    };

    if (totalOvertimeMinutes > 0) {
        updateData.overtime = {
            isOvertime: true,
            minutes: totalOvertimeMinutes,
            approved: false,
            approvedBy: null,
            approvedAt: null
        };
    }

    await updateDoc(docRef, updateData);

    return { id: docId, ...data, ...updateData };
};

// Get current session duration
export const getCurrentSessionDuration = (attendance) => {
    if (!attendance?.sessions) return 0;
    const activeSession = getActiveSession(attendance.sessions);
    if (!activeSession) return 0;

    const now = Date.now();
    const checkInTime = activeSession.checkIn.time.seconds * 1000;
    const breakMinutes = (activeSession.breaks || [])
        .filter(b => b.end)
        .reduce((sum, b) => sum + (b.duration || 0), 0);

    const totalMinutes = (now - checkInTime) / (1000 * 60);
    return Math.round(totalMinutes - breakMinutes);
};

// Get attendance for a specific date
export const getAttendanceByDate = async (userId, date) => {
    const docId = `${userId}_${date}`;
    const docRef = doc(db, ATTENDANCE_COLLECTION, docId);
    const snapshot = await getDoc(docRef);

    if (snapshot.exists()) {
        return { id: snapshot.id, ...snapshot.data() };
    }
    return null;
};

// Get attendance history for a user
export const getUserAttendanceHistory = async (userId, days = 30) => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    const startDateStr = startDate.toISOString().split('T')[0];

    const q = query(
        collection(db, ATTENDANCE_COLLECTION),
        where("userId", "==", userId),
        where("date", ">=", startDateStr),
        orderBy("date", "desc"),
        limit(30)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Get all attendance for a date (Admin)
export const getAttendanceByDateAdmin = async (date) => {
    const q = query(
        collection(db, ATTENDANCE_COLLECTION),
        where("date", "==", date),
        orderBy("userId")
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Get all users checked in
export const getCheckedInUsers = async () => {
    const q = query(
        collection(db, ATTENDANCE_COLLECTION),
        where("date", "==", getTodayString()),
        where("status", "in", ["checked-in", "on-break"])
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Auto check-out all users at 10 PM
export const autoCheckOutAll = async () => {
    const checkedInUsers = await getCheckedInUsers();
    const results = [];

    for (const record of checkedInUsers) {
        try {
            const result = await checkOut(record.userId, null, true);
            results.push({ userId: record.userId, success: true, result });
        } catch (error) {
            results.push({ userId: record.userId, success: false, error: error.message });
        }
    }

    return results;
};

// Admin: Approve overtime
export const approveOvertime = async (userId, date) => {
    const docId = `${userId}_${date}`;
    const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) {
        throw new Error("Attendance record not found");
    }

    await updateDoc(docRef, {
        "overtime.approved": true,
        "overtime.approvedAt": Timestamp.now(),
        "overtime.requested": false,
        "overtime.isOvertime": true,
        updatedAt: Timestamp.now()
    });
};

// Admin: Get overtime requests
export const getOvertimeRequests = async () => {
    const q = query(
        collection(db, ATTENDANCE_COLLECTION),
        where("overtime.isOvertime", "==", true),
        where("overtime.approved", "==", false)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};


// User: Request overtime manually
// export const requestOvertime = async (userId, date) => {
//     const docId = `${userId}_${date}`;
//     const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

//     const snapshot = await getDoc(docRef);
//     if (!snapshot.exists()) {
//         throw new Error("Attendance record not found");
//     }

//     const data = snapshot.data();

//     // Check if already requested
//     if (data.overtime?.requested) {
//         throw new Error("Overtime already requested");
//     }

//     // Calculate total work duration from sessions
//     const totalWork = data.totalWorkDuration || 0;
//     const regularHours = 8 * 60; // 8 hours in minutes
//     const overtimeMinutes = Math.max(0, totalWork - regularHours);

//     if (overtimeMinutes < 30) {
//         throw new Error("Minimum 30 minutes overtime required");
//     }

//     await updateDoc(docRef, {
//         "overtime": {
//             requested: true,
//             isOvertime: false,
//             minutes: overtimeMinutes,
//             approved: false,
//             approvedBy: null,
//             approvedAt: null,
//             rejected: false,
//             rejectedAt: null,
//             requestedAt: Timestamp.now()
//         },
//         updatedAt: Timestamp.now()
//     });

//     return { ...data, overtime: { requested: true, minutes: overtimeMinutes, approved: false } };
// };
// User: Request overtime manually
export const requestOvertime = async (userId, date = getTodayString()) => {
    const docId = `${userId}_${date}`;
    const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) {
        throw new Error("Attendance record not found");
    }

    const data = snapshot.data();

    // Check if already requested or approved
    if (data.overtime?.requested) {
        throw new Error("Overtime already requested");
    }
    if (data.overtime?.approved) {
        throw new Error("Overtime already approved");
    }

    // Calculate total work duration from sessions
    const totalWork = data.totalWorkDuration || 0;
    const regularHours = 8 * 60; // 8 hours in minutes
    const overtimeMinutes = Math.max(0, totalWork - regularHours);

    if (overtimeMinutes < 30) {
        throw new Error("Minimum 30 minutes overtime required");
    }

    await updateDoc(docRef, {
        "overtime": {
            requested: true,
            isOvertime: false,
            minutes: overtimeMinutes,
            approved: false,
            approvedBy: null,
            approvedAt: null,
            rejected: false,
            rejectedAt: null,
            requestedAt: Timestamp.now()
        },
        updatedAt: Timestamp.now()
    });

    return {
        ...data,
        overtime: {
            requested: true,
            minutes: overtimeMinutes,
            approved: false,
            rejected: false
        }
    };
};

// Admin: Reject overtime
export const rejectOvertime = async (userId, date) => {
    const docId = `${userId}_${date}`;
    const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) {
        throw new Error("Attendance record not found");
    }

    await updateDoc(docRef, {
        "overtime.requested": false,
        "overtime.isOvertime": false,
        "overtime.minutes": 0,
        "overtime.rejected": true,
        "overtime.rejectedAt": Timestamp.now(),
        updatedAt: Timestamp.now()
    });
};

// Add to attendanceService.js

// Get overtime status for a user on a specific date
export const getOvertimeStatus = async (userId, date = getTodayString()) => {
    const docId = `${userId}_${date}`;
    const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) {
        return {
            requested: false,
            approved: false,
            rejected: false,
            minutes: 0,
            isOvertime: false
        };
    }

    const data = snapshot.data();
    const overtime = data.overtime || {};

    return {
        requested: overtime.requested || false,
        approved: overtime.approved || false,
        rejected: overtime.rejected || false,
        minutes: overtime.minutes || 0,
        isOvertime: overtime.isOvertime || false,
        approvedAt: overtime.approvedAt || null,
        rejectedAt: overtime.rejectedAt || null,
        requestedAt: overtime.requestedAt || null
    };
};


// =======================================================================================

// Save periodic location update
export const saveLocationUpdate = async (userId, location, timestamp = Timestamp.now()) => {
    const today = getTodayString();
    const docId = `${userId}_${today}`;
    const locationRef = doc(db, ATTENDANCE_COLLECTION, docId, 'locations', `${timestamp.seconds}`);

    await setDoc(locationRef, {
        location: location,
        timestamp: timestamp,
        deviceInfo: navigator.userAgent || null
    });
};

// Get location history for a day
export const getLocationHistory = async (userId, date = getTodayString()) => {
    const docId = `${userId}_${date}`;
    const locationsRef = collection(db, ATTENDANCE_COLLECTION, docId, 'locations');
    const q = query(locationsRef, orderBy('timestamp', 'asc'));

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Start periodic location tracking
export const startPeriodicLocationTracking = (userId, callback) => {
    let intervalId = null;

    const trackLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const location = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        speed: position.coords.speed,
                        heading: position.coords.heading
                    };

                    await saveLocationUpdate(userId, location);
                    if (callback) callback(location);
                },
                (error) => {
                    console.warn('Location tracking error:', error);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 30000,
                    maximumAge: 60000
                }
            );
        }
    };

    // Track immediately
    trackLocation();

    // Track every 5 minutes while checked in
    intervalId = setInterval(trackLocation, 5 * 60 * 1000);

    return () => {
        if (intervalId) {
            clearInterval(intervalId);
        }
    };
};

// =======================================================================================
// Get location name from coordinates
export const getLocationName = async (latitude, longitude) => {
    try {
        const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
        );
        const data = await response.json();
        if (data && data.display_name) {
            return data.display_name;
        }
        return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
    } catch (error) {
        console.error('Error getting location name:', error);
        return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
    }
};

// // services/attendanceService.js

// import {
//     collection,
//     doc,
//     getDoc,
//     getDocs,
//     setDoc,
//     updateDoc,
//     query,
//     where,
//     Timestamp,
//     orderBy,
//     limit
// } from "firebase/firestore";
// import { db } from "../network/firebaseConfig";

// const ATTENDANCE_COLLECTION = "attendance";

// // Get today's date string
// export const getTodayString = () => {
//     const today = new Date();
//     return today.toISOString().split('T')[0]; // YYYY-MM-DD
// };

// // Get or create today's attendance record
// export const getOrCreateTodayAttendance = async (user) => {
//     const today = getTodayString();
//     const docId = `${user.uid}_${today}`;
//     const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

//     const snapshot = await getDoc(docRef);

//     if (snapshot.exists()) {
//         return { id: snapshot.id, ...snapshot.data() };
//     }

//     // Create new attendance record
//     const newRecord = {
//         userId: user.uid,
//         userName: user.name || "Unknown",
//         email: user.email,
//         date: today,
//         checkIn: null,
//         checkOut: null,
//         breaks: [],
//         totalBreakTime: 0,
//         workDuration: 0,
//         overtime: {
//             isOvertime: false,
//             minutes: 0,
//             approved: false,
//             approvedBy: null,
//             approvedAt: null
//         },
//         status: "not-checked-in",
//         createdAt: Timestamp.now(),
//         updatedAt: Timestamp.now()
//     };

//     await setDoc(docRef, newRecord);
//     return { id: docId, ...newRecord };
// };

// // Check-in
// export const checkIn = async (userId, location, deviceInfo) => {
//     const today = getTodayString();
//     const docId = `${userId}_${today}`;
//     const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

//     const snapshot = await getDoc(docRef);
//     const existing = snapshot.exists() ? snapshot.data() : null;

//     if (existing && existing.checkIn) {
//         throw new Error("Already checked in today");
//     }

//     const checkInData = {
//         time: Timestamp.now(),
//         location: location || null,
//         deviceInfo: deviceInfo || null
//     };

//     if (existing) {
//         await updateDoc(docRef, {
//             checkIn: checkInData,
//             status: "checked-in",
//             updatedAt: Timestamp.now()
//         });
//         return { id: docId, ...existing, checkIn: checkInData, status: "checked-in" };
//     } else {
//         const newRecord = {
//             userId: userId,
//             date: today,
//             checkIn: checkInData,
//             checkOut: null,
//             breaks: [],
//             totalBreakTime: 0,
//             workDuration: 0,
//             overtime: {
//                 isOvertime: false,
//                 minutes: 0,
//                 approved: false,
//                 approvedBy: null,
//                 approvedAt: null
//             },
//             status: "checked-in",
//             createdAt: Timestamp.now(),
//             updatedAt: Timestamp.now()
//         };
//         await setDoc(docRef, newRecord);
//         return { id: docId, ...newRecord };
//     }
// };

// // Start break
// export const startBreak = async (userId) => {
//     const today = getTodayString();
//     const docId = `${userId}_${today}`;
//     const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

//     const snapshot = await getDoc(docRef);
//     if (!snapshot.exists()) {
//         throw new Error("No attendance record found. Please check-in first.");
//     }

//     const data = snapshot.data();
//     if (data.status !== "checked-in") {
//         throw new Error("You must be checked in to take a break");
//     }

//     // Check if there's an ongoing break
//     const activeBreak = data.breaks?.find(b => !b.end);
//     if (activeBreak) {
//         throw new Error("You're already on a break");
//     }

//     const newBreak = {
//         start: Timestamp.now(),
//         end: null,
//         duration: 0
//     };

//     const breaks = [...(data.breaks || []), newBreak];

//     await updateDoc(docRef, {
//         breaks: breaks,
//         status: "on-break",
//         updatedAt: Timestamp.now()
//     });

//     return { ...data, breaks, status: "on-break" };
// };

// // End break
// export const endBreak = async (userId) => {
//     const today = getTodayString();
//     const docId = `${userId}_${today}`;
//     const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

//     const snapshot = await getDoc(docRef);
//     if (!snapshot.exists()) {
//         throw new Error("No attendance record found");
//     }

//     const data = snapshot.data();
//     if (data.status !== "on-break") {
//         throw new Error("You're not on a break");
//     }

//     const breaks = data.breaks || [];
//     const lastBreakIndex = breaks.length - 1;
//     const lastBreak = breaks[lastBreakIndex];

//     if (lastBreak.end) {
//         throw new Error("No active break found");
//     }

//     const endTime = Timestamp.now();
//     const startTime = lastBreak.start;
//     const durationMinutes = (endTime.seconds - startTime.seconds) / 60;

//     breaks[lastBreakIndex] = {
//         ...lastBreak,
//         end: endTime,
//         duration: Math.round(durationMinutes)
//     };

//     // Calculate total break time
//     const totalBreakMinutes = breaks.reduce((sum, b) => sum + (b.duration || 0), 0);

//     await updateDoc(docRef, {
//         breaks: breaks,
//         totalBreakTime: totalBreakMinutes,
//         status: "checked-in",
//         updatedAt: Timestamp.now()
//     });

//     return { ...data, breaks, totalBreakTime: totalBreakMinutes, status: "checked-in" };
// };

// // Check-out (Leave)
// export const checkOut = async (userId, location, isAuto = false) => {
//     const today = getTodayString();
//     const docId = `${userId}_${today}`;
//     const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

//     const snapshot = await getDoc(docRef);
//     if (!snapshot.exists()) {
//         throw new Error("No attendance record found");
//     }

//     const data = snapshot.data();

//     if (data.status === "checked-out") {
//         throw new Error("Already checked out today");
//     }

//     // If on break, end break first
//     if (data.status === "on-break") {
//         // Auto-end the break
//         const breaks = data.breaks || [];
//         const lastBreakIndex = breaks.length - 1;
//         const lastBreak = breaks[lastBreakIndex];
//         if (lastBreak && !lastBreak.end) {
//             const endTime = Timestamp.now();
//             const durationMinutes = (endTime.seconds - lastBreak.start.seconds) / 60;
//             breaks[lastBreakIndex] = {
//                 ...lastBreak,
//                 end: endTime,
//                 duration: Math.round(durationMinutes)
//             };
//             data.breaks = breaks;
//             data.totalBreakTime = breaks.reduce((sum, b) => sum + (b.duration || 0), 0);
//         }
//     }

//     const checkOutData = {
//         time: Timestamp.now(),
//         location: location || null,
//         isAuto: isAuto
//     };

//     // Calculate work duration (excluding breaks)
//     const checkInTime = data.checkIn.time;
//     const checkOutTime = checkOutData.time;
//     const totalMinutes = (checkOutTime.seconds - checkInTime.seconds) / 60;
//     const workMinutes = Math.round(totalMinutes - (data.totalBreakTime || 0));

//     // Check if it's overtime (after 10 PM)
//     const checkOutHour = new Date(checkOutTime.seconds * 1000).getHours();
//     const isOvertime = checkOutHour >= 22; // 10 PM or later

//     const updateData = {
//         checkOut: checkOutData,
//         workDuration: workMinutes,
//         status: "checked-out",
//         updatedAt: Timestamp.now()
//     };

//     if (isOvertime) {
//         // Overtime minutes (after 10 PM)
//         const overtimeMinutes = (checkOutTime.seconds - new Date(checkOutTime.seconds * 1000).setHours(22, 0, 0, 0) / 1000) / 60;
//         updateData.overtime = {
//             isOvertime: true,
//             minutes: Math.round(overtimeMinutes),
//             approved: false,
//             approvedBy: null,
//             approvedAt: null
//         };
//     }

//     await updateDoc(docRef, updateData);

//     return { id: docId, ...data, ...updateData };
// };

// // Get attendance for a specific date
// export const getAttendanceByDate = async (userId, date) => {
//     const docId = `${userId}_${date}`;
//     const docRef = doc(db, ATTENDANCE_COLLECTION, docId);
//     const snapshot = await getDoc(docRef);

//     if (snapshot.exists()) {
//         return { id: snapshot.id, ...snapshot.data() };
//     }
//     return null;
// };

// // Get attendance history for a user
// export const getUserAttendanceHistory = async (userId, days = 30) => {
//     const startDate = new Date();
//     startDate.setDate(startDate.getDate() - days);
//     const startDateStr = startDate.toISOString().split('T')[0];

//     const q = query(
//         collection(db, ATTENDANCE_COLLECTION),
//         where("userId", "==", userId),
//         where("date", ">=", startDateStr),
//         orderBy("date", "desc"),
//         limit(30)
//     );

//     const snapshot = await getDocs(q);
//     return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
// };

// // Get all attendance for a date (Admin)
// export const getAttendanceByDateAdmin = async (date) => {
//     const q = query(
//         collection(db, ATTENDANCE_COLLECTION),
//         where("date", "==", date),
//         orderBy("userId")
//     );

//     const snapshot = await getDocs(q);
//     return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
// };

// // Get all users checked in
// export const getCheckedInUsers = async () => {
//     const q = query(
//         collection(db, ATTENDANCE_COLLECTION),
//         where("date", "==", getTodayString()),
//         where("status", "in", ["checked-in", "on-break"])
//     );

//     const snapshot = await getDocs(q);
//     return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
// };

// // Auto check-out all users at 10 PM
// export const autoCheckOutAll = async () => {
//     const checkedInUsers = await getCheckedInUsers();
//     const results = [];

//     for (const record of checkedInUsers) {
//         try {
//             const result = await checkOut(record.userId, null, true);
//             results.push({ userId: record.userId, success: true, result });
//         } catch (error) {
//             results.push({ userId: record.userId, success: false, error: error.message });
//         }
//     }

//     return results;
// };

// // Admin: Approve overtime
// export const approveOvertime = async (userId, date) => {
//     const docId = `${userId}_${date}`;
//     const docRef = doc(db, ATTENDANCE_COLLECTION, docId);

//     const snapshot = await getDoc(docRef);
//     if (!snapshot.exists()) {
//         throw new Error("Attendance record not found");
//     }

//     await updateDoc(docRef, {
//         "overtime.approved": true,
//         "overtime.approvedAt": Timestamp.now(),
//         updatedAt: Timestamp.now()
//     });
// };

// // Admin: Get overtime requests
// export const getOvertimeRequests = async () => {
//     const q = query(
//         collection(db, ATTENDANCE_COLLECTION),
//         where("overtime.isOvertime", "==", true),
//         where("overtime.approved", "==", false)
//     );

//     const snapshot = await getDocs(q);
//     return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
// };